import React, { useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  CustomSelect,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  SearchBar,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  Typography,
  MUIDataTableColumn
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import { useDataTable } from 'web-library/stories/organism/DataTable/DataTable';
import { customFilterChipRender } from '../../../../hooks/table';
import { dropDownMappingToSelect, useAPIDropdown } from '../../../../hooks/dropdown';
import {
  PostSearchEntryProofResponse,
  usePostSearchEntryProof,
} from '../../../../api-refactored/entry-proof/usePostSearchEntryProof';
import { FileUploadState } from '../../../../models/File';
import { RetrieveS3URLResponse } from '../../../../api/document/files/api-services/model';
import DownloadFromS3Action from '../../../../components/buttons/downloadFromS3Action';
import DownloadAuditProgressModal from '../../../../components/modals/DownloadAuditProgressModal';
import {
  PostDownloadEntryProofRequest,
  usePostDownloadEntryProof,
} from '../../../../api-refactored/entry-proof/usePostDownloadEntryProof';
import { getIndexValue } from '../../../../utils/ArrayUtils';
import { stringToLongDateAndShortTime, utcToDefaultDisplay } from '../../../../utils/DateUtils';
import { useGetCoordinatorExamLevels } from '../../../../api-refactored/entry-proof/useGetCoordinatorExamLevels';

const useStyles = makeStyles()((theme) =>
({
  tableSearchContainer: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(2),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  textDisabled: {
    color: theme.color.text.textDisabled,
  },
}));

const useRawFilters = () => {
  const examLevels = useAPIDropdown(useGetCoordinatorExamLevels, true, dropDownMappingToSelect);

  return {
    examLevels,
  };
};

const isDisabled = (data: PostSearchEntryProofResponse[], dataIndex: number) =>
  !data[dataIndex].lastUpdateDateTime;

const useTableColumns = ({
  classes,
  t,
  tableState,
  tableDispatch,
  examLevels,
}: {
  classes: any;
  t: TFunction;
  tableState: TableState;
  tableDispatch: React.Dispatch<TableActionTypes>;
  examLevels: { functionCode: any; typeMapping: { [key: string]: any } };
}): MUIDataTableColumnDef[] => {
  const { typeMapping: filterMappingExamLevel, functionCode: filterCodeExamLevel } = examLevels;

  return [
    {
      name: 'uinFinPassport',
      label: t('entryProof->>MakingRequestListing.UinFinOrPassport'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isDisabled(tableState.data, dataIndex) ? classes.textDisabled : ''}
          >
            {tableState.data[dataIndex].uinFinPassport}
          </Typography>
        ),
      },
    },
    {
      name: 'name',
      label: t('entryProof->>MakingRequestListing.CandidateName'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isDisabled(tableState.data, dataIndex) ? classes.textDisabled : ''}
          >
            {tableState.data[dataIndex].name}
          </Typography>
        ),
      },
    },
    {
      name: 'examLevel',
      label: t('entryProof->>MakingRequestListing.ExamLevel'),
      options: {
        sort: false,
        searchable: false,
        filter: true,
        filterType: 'custom',
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isDisabled(tableState.data, dataIndex) ? classes.textDisabled : ''}
          >
            {tableState.data[dataIndex].examLevel}
          </Typography>
        ),
        customFilterListOptions: {
          render: (v: any) => [filterMappingExamLevel[v[0]].children],
          update: (filterList: string[][],
            filterPos: number,
            index: number) => {
            // update table filter state
            tableDispatch({
              type: 'UPDATE_FILTERS',
              payload: { ...tableState.filters, examLevel: [] },
            });

            // update table inner filter
            return customFilterChipRender(filterList, filterPos, index);
          },
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              // eslint-disable-next-line react/destructuring-assignment
              value={filterList[index][0]}
              displayEmpty
              menuItems={filterCodeExamLevel}
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;
                onChange(filterList[index], index, column);
              }}
              label={t('entryProof->>MakingRequestListing.ExamLevel')}
              labelId="inputSelectLabel-examLevel"
              id="inputSelect-examLevel"
            />
          ),
        },
      },
    },
    {
      name: 'lastUpdateDateTime',
      label: t('entryProof->>MakingRequestListing.LastUpdateDateTime'),
      options: {
        sort: false,
        searchable: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => {
          const lastUpdateDateTime = tableState.data[dataIndex].lastUpdateDateTime
            ? stringToLongDateAndShortTime(
              utcToDefaultDisplay(tableState.data[dataIndex].lastUpdateDateTime)
            )
            : '-';

          return (
            <Typography
              variant="body2"
              className={isDisabled(tableState.data, dataIndex) ? classes.textDisabled : ''}
            >
              {lastUpdateDateTime}
            </Typography>
          );
        },
      },
    },
    {
      name: 'entryProofType',
      label: t('entryProof->>MakingRequestListing.EntryProofType'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => {
          const entryProofType = tableState.data[dataIndex].entryProofType
            ? tableState.data[dataIndex].entryProofType
            : '-';
          return (
            <Typography
              variant="body2"
              className={isDisabled(tableState.data, dataIndex) ? classes.textDisabled : ''}
            >
              {entryProofType}
            </Typography>
          );
        },
      },
    },
  ];
};

const useFileDownloadAction = (data: PostSearchEntryProofResponse[] | undefined) => {
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [dialogFileProcessingState, setDialogFileProcessing] = useState<boolean>(false);

  const [fileUUID, setFileUUID] = useState('');

  const { mutate: downloadMutate } = usePostDownloadEntryProof({
    onSuccess: (response) => {
      setFileUUID(response.uuid);
    },
  });

  /** to define bulk download
   * */
  const onHandleExport = (inputRequest: PostDownloadEntryProofRequest) => {
    setDialogFileProcessing(true);
    downloadMutate(inputRequest);
  };

  /** handle download action
   * */
  const downloadFile = (downloadResponse: RetrieveS3URLResponse) => {
    setDialogFileProcessing(true);
    if (downloadResponse.fileStatus === FileUploadState.SUCCESS) {
      setFileUUID('');
      setDialogFileProcessing(false);
    }
    setFileDownloadStatus(downloadResponse.fileStatus as FileUploadState);
  };

  /** handle dialog close
   * */
  const onDialogDownloadClose = () => {
    setDialogFileProcessing(false);
    setFileUUID('');
  };

  return {
    dialogFileProcessingState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadFile,
    onDialogDownloadClose,
    disableExportButton: dialogFileProcessingState || data === undefined || data.length === 0,
  };
};

const useChangeDownloadActions = (
  tableState: TableState,
  t: TFunction,
  exportAction: {
    onHandleExport: (input: PostDownloadEntryProofRequest) => void;
    fileUUID: string;
    downloadFile: (status: RetrieveS3URLResponse) => void;
    disableExportButton: boolean;
  }
) =>
  useMemo(
    () => [
      <DownloadFromS3Action
        color="primary"
        actionType="link"
        requestRoute={{ data: exportAction.fileUUID, url: '/api/s3/files' }}
        disable={exportAction.disableExportButton}
        key="download-s3-action"
        onClick={() => {
          const selectedList: string[] = Object.keys(tableState.rowsSelected).map(
            (s) => tableState.rowsSelected[s].uuid
          );
          exportAction.onHandleExport({
            uuid: selectedList,
          });
        }}
        onDownload={(status) => exportAction.downloadFile(status)}
        label={t('entryProof->>MakingRequestListing.DownloadSelections')}
      />,
    ],
    [exportAction, tableState.rowsSelected, t]
  );

const EntryProofSearchFilterTable = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { state: tableState, dispatch: tableDispatch, tOptions, selectionToolbar } = useDataTable({
    ...TABLE_INITIAL_STATE,
    size: 10,
  });

  const { examLevels } = useRawFilters();

  const { mutate: searchMutate, isLoading: searchLoading } = usePostSearchEntryProof({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  const {
    dialogFileProcessingState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadFile,
    onDialogDownloadClose,
    disableExportButton,
  } = useFileDownloadAction(tableState.data);

  const changeButtonComponent = useChangeDownloadActions(tableState, t, {
    onHandleExport,
    fileUUID,
    downloadFile,
    disableExportButton,
  });

  useEffect(() => {
    searchMutate({
      body: { q: tableState.search, examLevelUuid: getIndexValue(tableState.filters.examLevel, 0) },
      apiParams: {
        order: tableState.order,
        sort: tableState.sortColumn,
        page: tableState.page,
        pageSize: tableState.size,
      },
    });
  }, [
    searchMutate,
    tableState.order,
    tableState.page,
    tableState.size,
    tableState.sortColumn,
    tableState.search,
    tableState.filters.examLevel,
  ]);

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      selectableRows: 'multiple',
      pagination: true,
      filter: true,
      sort: true,
      filterType: 'multiselect',
      selectToolbarPlacement: 'replace',
      isRowSelectable: (dataIndex: number) => !isDisabled(tableState.data, dataIndex),
    }),
    [tOptions, tableState.data]
  );

  return (
    <>
      <Box className={classes.tableSearchContainer}>
        <SearchBar
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue=""
          placeholder={t('entryProof->>MakingRequestListing.SearchBarPlaceholder')}
          onSearch={(str) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: str.trim(),
            });
          }}
        />
      </Box>

      <Box
          // className={classes.tableContainer}
          >
        <MUIDataTable
          isLoading={searchLoading}
          columns={useTableColumns({
            classes,
            t,
            tableState,
            tableDispatch,
            examLevels,
          })}
          data={tableState.data}
          dispatch={tableDispatch}
          state={tableState}
          options={customOptions}
          rowsPerPageOptions={[10, 20, 30, 40, 50]}
          components={{ TableToolbarSelect: () => selectionToolbar(changeButtonComponent) }}
        />
      </Box>

      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogFileProcessingState}
        onDialogClose={onDialogDownloadClose}
      />
    </>
  );
};

export default EntryProofSearchFilterTable;
